import React, { useEffect, useRef, useState } from "react";
import { Button, Dialog } from "components/ui";
import {
    HiOutlineClock,
    HiOutlineLogout,
    HiShieldCheck,
    HiExclamation,
} from "react-icons/hi";

const SESSION_IMAGE_SRC = "/img/others/session-image.png";

const SessionExpiredDialog = ({
    isOpen,
    loading = false,
    title = "Session expired",
    message = "You've been inactive for a while. Do you want to stay signed in?",
    autoLogoutSeconds = 300,
    onStay,
    onLogOut,
}) => {
    const [secondsLeft, setSecondsLeft] = useState(autoLogoutSeconds);
    const autoLogoutTriggeredRef = useRef(false);
    const onLogOutRef = useRef(onLogOut);
    const deadlineRef = useRef(null);

    useEffect(() => {
        onLogOutRef.current = onLogOut;
    }, [onLogOut]);

    useEffect(() => {
        if (!isOpen) {
            deadlineRef.current = null;
            autoLogoutTriggeredRef.current = false;
            setSecondsLeft(autoLogoutSeconds);
            return;
        }

        if (!deadlineRef.current) {
            deadlineRef.current = Date.now() + autoLogoutSeconds * 1000;
            autoLogoutTriggeredRef.current = false;
            setSecondsLeft(autoLogoutSeconds);
        }

        if (loading) {
            return;
        }

        const timer = setInterval(() => {
            const remaining = Math.max(
                0,
                Math.ceil((deadlineRef.current - Date.now()) / 1000),
            );
            setSecondsLeft(remaining);

            if (remaining > 0 || autoLogoutTriggeredRef.current) {
                return;
            }

            autoLogoutTriggeredRef.current = true;
            clearInterval(timer);
            onLogOutRef.current?.();
        }, 250);

        return () => clearInterval(timer);
    }, [autoLogoutSeconds, isOpen, loading]);

    return (
        <Dialog
            isOpen={isOpen}
            closable={false}
            width={900}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            overlayClassName="!bg-black/75 backdrop-blur-sm"
            contentClassName="!p-0 overflow-hidden !rounded-3xl !bg-white dark:!bg-[#161616] !border !border-gray-200 dark:!border-gray-700"
        >
            <div className="grid grid-cols-[360px_1fr] min-h-[470px]">

                {/* Left Side */}

                <div className="flex items-center justify-center border-r border-gray-200 dark:border-gray-700 px-10">

                    <img
                        src={SESSION_IMAGE_SRC}
                        alt="Session"
                        className="w-[660px] max-w-none object-contain"
                        draggable={false}
                        
                    />

                </div>

                {/* Right Side */}

                <div className="flex flex-col justify-center px-12 py-10">

                    <h2 className="flex items-center gap-3 text-[48px] font-bold leading-none text-gray-900 dark:text-white">
                        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-orange-500/25 bg-orange-500/15">
                            <HiExclamation className="h-8 w-8 text-orange-500 dark:text-orange-400" />
                        </span>
                        <span>{title}</span>
                    </h2>

                    <p className="mt-6 max-w-lg text-[22px] leading-9 text-gray-600 dark:text-gray-400">
                        {message}
                    </p>

                    <div className="mt-10 flex gap-5">

                        <Button
                            variant="plain"
                            size="lg"
                            icon={<HiOutlineLogout />}
                            disabled={loading}
                            onClick={onLogOut}
                            className="!h-14 !px-8 !rounded-xl !border !border-gray-300 dark:!border-gray-600 !bg-transparent !text-gray-900 dark:!text-white hover:!bg-gray-100 dark:hover:!bg-[#1f1f1f]"
                        >
                            Log out
                        </Button>

                        <Button
                            variant="solid"
                            size="lg"
                            color="green-600"
                            icon={<HiShieldCheck />}
                            loading={loading}
                            onClick={onStay}
                            className="!h-14 !px-8 !rounded-xl"
                        >
                            Stay signed in
                        </Button>

                    </div>

                    <div className="mt-10 flex items-center gap-3 text-gray-500 dark:text-gray-400">

                        <HiOutlineClock className="h-6 w-6" />

                        {loading ? (
                            <span className="text-[15px]">
                                Renewing your session...
                            </span>
                        ) : (
                            <span className="text-[15px]">
                                You will be logged out automatically due to inactivity in{" "}
                                <span className="font-semibold text-blue-600 dark:text-blue-500">
                                    {secondsLeft}
                                </span>{" "}
                                seconds.
                            </span>
                        )}

                    </div>

                </div>

            </div>
        </Dialog>
    );
};

export default SessionExpiredDialog;
